import { forwardRef, useEffect, useImperativeHandle, useState, useRef } from 'react';
import dayjs from 'dayjs';
import jsQR from 'jsqr';
import {
  Box,
  Switch,
  FormControlLabel,
  TextField,
  Typography,
  IconButton,
  Tooltip,
  Grid,
  Autocomplete,
  InputAdornment
} from '@mui/material';
import PickerField from '../../../../components/PickerField';
import { getVehicleImage, getVehicleBarcodeImage } from '../../../../stores/masterVehicleStore';
import { useLanguage } from '../../../../utils/i18n';
import { NavigateBefore, NavigateNext, Close } from '@mui/icons-material';
import DeleteConfirmDialog from '../../../../components/DeleteConfirmDialog';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { readOnlyFieldStyle } from '../../../dashboard/fuel/constant';

import { useCompanyDataList } from '../../../../stores/companyStore';
import { useVehicleTypeList } from '../../../../stores/vehicleTypeStore';

import { useVendorList } from '../../../../stores/vendorStore';
import { useMasterFuelPriceList } from '../../../../stores/masterFuelPriceStore';
import usePickerPagination from '../../../../hooks/usePickerPagination';
import PickerFieldTable from '../../../../components/PickerFieldTable';
import MasterFuelPriceAddEdit from '../../../master-data/master-fuel-price/MasterFuelPriceAddEdit';

import VehicleTypeAddEdit from '../../vehicle-type/VehicleTypeAddEdit';

import VendorAddEdit from '../../vendor/VendorAddEdit';
import { formatUpperCase } from '../../../../utils/formatTitleCase';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import FuelInformationDialog from './FuelInformationDialog';

const InformationForm = forwardRef(({ formik, companyOptionsFromParent = [] }, ref) => {
  const { values, touched, errors, handleChange, handleBlur, setFieldValue } = formik;

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 50 }, (_, i) => String(currentYear - i));
  const { t } = useLanguage();
  const [fuelHistoryDialogOpen, setFuelHistoryDialogOpen] = useState(false);

  // Hooks are replaced with usePickerPagination below

  const [filterCompanyId, setFilterCompanyId] = useState('');

  useEffect(() => {
    const currentCompany = values.company ? String(values.company) : '';
    if (currentCompany !== filterCompanyId) {
      setFilterCompanyId(currentCompany);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.company]);

  const [vehicleImages, setVehicleImages] = useState([]);
  const [vehiclePreview, setVehiclePreview] = useState([]);

  const uniqueByValue = (arr = []) => {
    const map = new Map();
    (arr || []).forEach((item) => {
      const key = String(item.value ?? item.id ?? item.name ?? item.label ?? '');
      if (!map.has(key)) map.set(key, item);
    });
    return Array.from(map.values());
  };

  const companyPickerParams = usePickerPagination({
    fetchHook: useCompanyDataList,
    buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue],
    searchKey: 'name'
  });
  const companyOptions =
    companyOptionsFromParent && companyOptionsFromParent.length ? companyOptionsFromParent : companyPickerParams.options;

  const vtPickerParams = usePickerPagination({
    fetchHook: useVehicleTypeList,
    buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue, state.companyId],
    searchKey: 'name',
    companyId: filterCompanyId,
    formatOption: (v) => ({ value: v.id, label: v.name ?? v.vehicle_type_name ?? String(v.id) })
  });
  const vehicleTypeOptions = uniqueByValue(vtPickerParams.options);

  const vendorPickerParams = usePickerPagination({
    fetchHook: useVendorList,
    buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue, state.companyId],
    searchKey: 'name',
    companyId: filterCompanyId
  });
  const vendorOptions = uniqueByValue(vendorPickerParams.options);

  const fuelPricePickerParams = usePickerPagination({
    fetchHook: useMasterFuelPriceList,
    buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue, state.companyId],
    companyId: filterCompanyId,
    formatOption: (p) => {
      const fuelName = p?.fuel_type_join?.name || p.fuel_type_name || p.name || '';
      const fuelCode = p?.fuel_type_join?.code || p.fuel_type_code || p.code || '';
      const fuelTypeId = p?.fuel_type_join?.id || p.fuel_type_id || (typeof p.fuel_type === 'object' ? p.fuel_type?.id : p.fuel_type) || '';
      const formattedPrice = p.price != null && p.price !== '' ? 'Rp ' + Number(p.price).toLocaleString('id-ID') : '';

      let labelStr = fuelName;
      if (fuelCode) labelStr += ` - ${fuelCode}`;
      if (fuelTypeId) labelStr += ` - ${fuelTypeId}`;
      if (formattedPrice) labelStr += ` - ${formattedPrice}`;

      return {
        value: p.id,
        label: labelStr || `Fuel ${p.id}`,
        original: p
      };
    },
    searchKey: 'all'
  });

  const selectedFuelPriceOption = values.fuel_price
    ? [
        {
          value: values.fuel_price,
          label: (() => {
            const fuelName = values.fuel_type_name || '';
            const fuelCode = values.fuel_type_code || '';
            const fuelTypeId = values.fuel_type || '';
            const formattedPrice =
              values.fuel_price_val != null && values.fuel_price_val !== ''
                ? 'Rp ' + Number(values.fuel_price_val).toLocaleString('id-ID')
                : '';
            let labelStr = fuelName;
            if (fuelCode) labelStr += ` - ${fuelCode}`;
            if (fuelTypeId) labelStr += ` - ${fuelTypeId}`;
            if (formattedPrice) labelStr += ` - ${formattedPrice}`;
            return labelStr || `Fuel Price ${values.fuel_price}`;
          })(),
          original: {
            id: values.fuel_price,
            fuel_type_name: values.fuel_type_name,
            price: values.fuel_price_val,
            valid_at: values.fuel_price_valid_at,
            fuel_type: values.fuel_type,
            fuel_type_category: values.fuel_type_category,
            fuel_type_code: values.fuel_type_code,
            fuel_type_desc: values.fuel_type_desc,
            fuel_type_uom: values.fuel_type_uom
          }
        }
      ]
    : [];

  const fuelPriceOptions = uniqueByValue(
    [...selectedFuelPriceOption, ...fuelPricePickerParams.options].filter((opt) => String(opt.original?.id) !== '0')
  );

  const [, setQrCodeImages] = useState([]);
  const [qrCodePreview, setQrCodePreview] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const [openDeletePhoto, setOpenDeletePhoto] = useState(false);
  const [openDeleteQr, setOpenDeleteQr] = useState(false);

  const handleGetImages = async (id) => {
    try {
      const response = await getVehicleImage(id);
      const normalizedImages = Array.isArray(response) ? response : typeof response === 'string' && response !== '' ? [response] : [];
      setVehiclePreview(normalizedImages);
      setVehicleImages(normalizedImages);
    } catch (error) {
      console.error('Failed to load images:', error);
    }
  };

  const handleVehicleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    setVehicleImages((prev) => [...prev, ...files]);
    const previews = files.map((f) => URL.createObjectURL(f));
    setVehiclePreview((prev) => [...prev, ...previews]);
    setFieldValue('vehicle_photo', [...vehicleImages, ...files]);
  };

  const handleRemoveClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setOpenDeletePhoto(true);
  };

  const handleConfirmRemove = () => {
    const targetImage = vehiclePreview[currentImageIndex];
    if (typeof targetImage === 'string' && targetImage.startsWith('http')) {
      const currentDeleted = values._deleted_vehicle_photos || [];
      setFieldValue('_deleted_vehicle_photos', [...currentDeleted, targetImage]);
    } else {
      const serverImageCount = vehiclePreview.filter((img) => typeof img === 'string' && img.startsWith('http')).length;
      if (currentImageIndex >= serverImageCount) {
        const localIndex = currentImageIndex - serverImageCount;
        setVehicleImages((prev) => prev.filter((_, i) => i !== localIndex));
        const currentFiles = values.vehicle_photo || [];
        setFieldValue(
          'vehicle_photo',
          currentFiles.filter((_, i) => i !== localIndex)
        );
      }
    }

    setVehiclePreview((prevImages) => {
      const newImages = prevImages.filter((_, idx) => idx !== currentImageIndex);
      setCurrentImageIndex((prevIndex) => {
        if (newImages.length === 0) return 0;
        if (prevIndex >= newImages.length) return newImages.length - 1;
        return prevIndex;
      });
      return newImages;
    });

    setOpenDeletePhoto(false);
  };

  const handleCancelRemove = () => setOpenDeletePhoto(false);

  useImperativeHandle(ref, () => ({
    getFiles: () => vehicleImages
  }));

  const scanQrFromFile = async (file) => {
    if (!file) return null;
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        try {
          const MAX_DIM = 1024;
          let w = img.naturalWidth;
          let h = img.naturalHeight;
          if (w > MAX_DIM || h > MAX_DIM) {
            const ratio = Math.min(MAX_DIM / w, MAX_DIM / h);
            w = Math.round(w * ratio);
            h = Math.round(h * ratio);
          }
          const canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, w, h);
          const imageData = ctx.getImageData(0, 0, w, h);
          if (typeof jsQR !== 'undefined') {
            const code = jsQR(imageData.data, imageData.width, imageData.height);
            if (code?.data) resolve(code.data);
            else resolve(null);
          } else {
            resolve(null);
          }
        } catch {
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      img.src = URL.createObjectURL(file);
    });
  };

  const handleGetImagesQrCode = async (id) => {
    try {
      const response = await getVehicleBarcodeImage(id);
      let imageUrl = response;
      const normalizedImages = imageUrl ? [imageUrl] : [];
      setQrCodePreview(normalizedImages);
      setQrCodeImages(normalizedImages);
      if (imageUrl) {
        setFieldValue('qr_pertamina_preview', imageUrl);
      }
    } catch (error) {
      if (error.response && error.response.status === 400) {
        setQrCodePreview([]);
        setQrCodeImages([]);
        setFieldValue('qr_pertamina_preview', null);
      } else {
        console.error('Failed to load QR images:', error);
      }
    }
  };

  useEffect(() => {
    if (values.id) {
      handleGetImages(values.id);
      handleGetImagesQrCode(values.id);

      // Format fuel ratio values on load
      if (values.fuel_ratio_km !== '' && values.fuel_ratio_km != null && !isNaN(values.fuel_ratio_km)) {
        const formattedKm = parseFloat(values.fuel_ratio_km).toFixed(2);
        if (String(values.fuel_ratio_km) !== formattedKm) {
          setFieldValue('fuel_ratio_km', formattedKm);
        }
      }
      if (values.fuel_ratio_litre !== '' && values.fuel_ratio_litre != null && !isNaN(values.fuel_ratio_litre)) {
        const formattedLitre = parseFloat(values.fuel_ratio_litre).toFixed(2);
        if (String(values.fuel_ratio_litre) !== formattedLitre) {
          setFieldValue('fuel_ratio_litre', formattedLitre);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.id]);

  const handleQrCodeFileChange = async (event) => {
    const files = Array.from(event.currentTarget.files || []);
    if (files.length === 0) return;
    const file = files[0];
    setQrCodeImages((prev) => [...prev, file]);
    const previewUrl = URL.createObjectURL(file);
    setQrCodePreview([previewUrl]);
    setFieldValue('qr_pertamina_file_obj', file);
    setFieldValue('qr_pertamina_preview', previewUrl);
    const scannedValue = await scanQrFromFile(file);
    if (scannedValue) {
      setFieldValue('qr_pertamina_value', scannedValue);
    }
  };

  const handleRemoveQrCodeClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setOpenDeleteQr(true);
  };

  const handleConfirmRemoveQrCode = () => {
    const isServerImage =
      values.id && qrCodePreview.length > 0 && typeof qrCodePreview[0] === 'string' && qrCodePreview[0].startsWith('http');
    setQrCodePreview([]);
    setQrCodeImages([]);
    setFieldValue('qr_pertamina_file_obj', null);
    setFieldValue('qr_pertamina_preview', null);
    setFieldValue('qr_pertamina_value', '');
    const inputElement = document.getElementById('qr-upload');
    if (inputElement) inputElement.value = '';
    if (isServerImage) {
      const urlToDelete = qrCodePreview[0];
      setFieldValue('_deleted_qr_url', urlToDelete);
      setFieldValue('_delete_qr_code', true);
    }
    setOpenDeleteQr(false);
  };

  const handleCancelRemoveQrCode = () => setOpenDeleteQr(false);

  const handleNumberChange = (name) => (e) => {
    const v = e.target.value;
    setFieldValue(name, v === '' ? '' : v);
  };

  const handleDecimalChange =
    (name, maxDecimals = 2) =>
    (e) => {
      let v = e.target.value;
      if (v === '') {
        setFieldValue(name, '');
        return;
      }
      if (v.includes('.')) {
        const split = v.split('.');
        if (split[1].length > maxDecimals) {
          v = `${split[0]}.${split[1].substring(0, maxDecimals)}`;
        }
      }
      setFieldValue(name, v);
    };

  const handleDecimalBlur =
    (name, maxDecimals = 2) =>
    (e) => {
      handleBlur(e);
      let v = e.target.value;
      if (v !== '' && !isNaN(v)) {
        setFieldValue(name, parseFloat(v).toFixed(maxDecimals));
      }
    };

  const syncedFuelPriceRef = useRef(null);

  useEffect(() => {
    if (!values.fuel_price) {
      syncedFuelPriceRef.current = null;
      return;
    }

    if (syncedFuelPriceRef.current === values.fuel_price) return;

    if (fuelPriceOptions.length > 0) {
      const selected = fuelPriceOptions.find((opt) => String(opt.value) === String(values.fuel_price));
      if (selected && selected.original) {
        syncedFuelPriceRef.current = values.fuel_price;
        const orig = selected.original;
        const fuelTypeId =
          (typeof orig.fuel_type === 'object' ? orig.fuel_type?.id : orig.fuel_type) ||
          orig.fuel_type_id ||
          orig?.fuel_type_join?.id ||
          orig?.join?.fuel_type_id ||
          '';

        setFieldValue('fuel_price_val', orig.price || 0);
        setFieldValue('fuel_price_desc', orig.description || '');
        setFieldValue('fuel_price_valid_at', orig.valid_at || '');
        if (fuelTypeId) setFieldValue('fuel_type', fuelTypeId);
        setFieldValue('fuel_type_category', orig.fuel_type_category || orig?.fuel_type_join?.category || '');
        setFieldValue('fuel_type_code', orig.fuel_type_code || orig?.fuel_type_join?.code || '');
        setFieldValue('fuel_type_desc', orig.fuel_type_desc || orig?.fuel_type_join?.description || '');
        setFieldValue('fuel_type_name', orig.fuel_type_name || orig?.fuel_type_join?.name || '');
        setFieldValue('fuel_type_uom', orig.fuel_type_uom || orig?.fuel_type_join?.uom_type || '');
      }
    }
  }, [values.fuel_price, fuelPriceOptions, setFieldValue]);

  const handleFuelTypeChange = (opt) => {
    const val = typeof opt === 'object' ? opt?.value : opt;
    const selected = fuelPriceOptions.find((o) => String(o.value) === String(val));

    if (selected && selected.original) {
      const orig = selected.original;
      const fuelTypeId =
        (typeof orig.fuel_type === 'object' ? orig.fuel_type?.id : orig.fuel_type) ||
        orig.fuel_type_id ||
        orig?.fuel_type_join?.id ||
        orig?.join?.fuel_type_id ||
        '';

      setFieldValue('fuel_price', orig.id || 0);
      setFieldValue('fuel_price_desc', orig.description || '');
      setFieldValue('fuel_price_val', orig.price || 0);
      setFieldValue('fuel_price_valid_at', orig.valid_at || '');
      setFieldValue('fuel_type', fuelTypeId || '');
      setFieldValue('fuel_type_category', orig.fuel_type_category || orig?.fuel_type_join?.category || '');
      setFieldValue('fuel_type_code', orig.fuel_type_code || orig?.fuel_type_join?.code || '');
      setFieldValue('fuel_type_desc', orig.fuel_type_desc || orig?.fuel_type_join?.description || '');
      setFieldValue('fuel_type_name', orig.fuel_type_name || orig?.fuel_type_join?.name || '');
      setFieldValue('fuel_type_uom', orig.fuel_type_uom || orig?.fuel_type_join?.uom_type || '');
    } else {
      setFieldValue('fuel_price', 0);
      setFieldValue('fuel_price_desc', '');
      setFieldValue('fuel_price_val', 0);
      setFieldValue('fuel_price_valid_at', '');
      setFieldValue('fuel_type', '');
      setFieldValue('fuel_type_category', '');
      setFieldValue('fuel_type_code', '');
      setFieldValue('fuel_type_desc', '');
      setFieldValue('fuel_type_name', '');
      setFieldValue('fuel_type_uom', '');
    }
  };

  const formatCurrency = (v) =>
    new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(Number(v || 0));

  return (
    <Grid container spacing={3}>
      <Grid size={{ xs: 6, md: 6 }}>
        <PickerField
          label={t('general.company', 'Company')}
          value={values.company}
          {...companyPickerParams}
          options={companyOptions}
          onChange={(opt) => {
            const val = typeof opt === 'object' ? opt?.value : opt;
            const selected = companyOptions.find((c) => String(c.value) === String(val)) ?? null;
            const id = selected ? selected.value : (val ?? null);

            if (String(values.company) !== String(id)) {
              setFieldValue('company', id);
              setFieldValue('company_name', selected ? selected.label : '');

              setFieldValue('vehicle_type', null);
              setFieldValue('vehicle_type_name', '');
              setFieldValue('vendor', null);
              setFieldValue('vendor_name', '');
              setFieldValue('fuel_price', null);
              setFieldValue('fuel_type', null);
              setFieldValue('fuel_price_val', '');
            }
          }}
          dialogTitle={t('general.selectCompany', 'Select Company')}
          required={true}
          error={touched.company && !!errors.company}
          helperText={touched.company && errors.company}
          style={readOnlyFieldStyle}
        />
      </Grid>
      <Grid size={{ xs: 6, md: 6 }}>
        <TextField
          label={t('masterVehicle.plateNumber', 'Plate Number')}
          name="license_plate"
          value={values.license_plate ?? ''}
          onChange={(e) => {
            setFieldValue('license_plate', formatUpperCase(e.target.value));
          }}
          onBlur={handleBlur}
          size="medium"
          fullWidth
          error={touched.license_plate && !!errors.license_plate}
          helperText={touched.license_plate && errors.license_plate}
          required={true}
          sx={readOnlyFieldStyle}
          InputLabelProps={{
            sx: { '& .MuiFormLabel-asterisk': { color: 'error.main' } }
          }}
        />
      </Grid>

      <Grid size={{ xs: 6, md: 6 }}>
        <TextField
          label={t('general.merk', 'Merk')}
          name="merk"
          value={values.merk ?? ''}
          onChange={handleChange}
          onBlur={handleBlur}
          size="medium"
          fullWidth
          sx={readOnlyFieldStyle}
          required
          InputLabelProps={{
            sx: { '& .MuiFormLabel-asterisk': { color: 'error.main' } }
          }}
        />
      </Grid>

      <Grid size={{ xs: 6, md: 6 }}>
        <PickerField
          label={t('general.vehicleType', 'Vehicle Type')}
          value={values.vehicle_type}
          {...vtPickerParams}
          options={vehicleTypeOptions}
          onChange={(opt) => {
            const val = typeof opt === 'object' ? opt?.value : opt;
            const selected = vehicleTypeOptions.find((c) => String(c.value) === String(val)) ?? null;
            const id = selected ? selected.value : (val ?? null);
            setFieldValue('vehicle_type', id);
            setFieldValue('vehicle_type_name', selected ? selected.label : '');
          }}
          allowAdd
          AddComponent={VehicleTypeAddEdit}
          dialogProps={{ fullWidth: true, maxWidth: 'sm' }}
          dialogTitle={t('general.selectVehicleType', 'Select Vehicle Type')}
          required={true}
          error={touched.vehicle_type && !!errors.vehicle_type}
          helperText={
            !values.company
              ? t('general.selectCompanyFirst', '* Please select a company first')
              : touched.vehicle_type && errors.vehicle_type
          }
          disabled={!values.company}
          style={readOnlyFieldStyle}
        />
      </Grid>
      <Grid size={{ xs: 6, md: 6 }}>
        <TextField
          label={t('general.model', 'Model')}
          name="model"
          value={values.model ?? ''}
          onChange={handleChange}
          onBlur={handleBlur}
          size="medium"
          fullWidth
          required
          sx={readOnlyFieldStyle}
          InputLabelProps={{
            sx: { '& .MuiFormLabel-asterisk': { color: 'error.main' } }
          }}
        />
      </Grid>
      <Grid size={{ xs: 6, md: 6 }}>
        <Autocomplete
          freeSolo
          options={yearOptions}
          value={values.year ? String(values.year) : ''}
          onChange={(e, newValue) => {
            setFieldValue('year', newValue ?? '');
          }}
          onInputChange={(e, newInputValue) => {
            setFieldValue('year', newInputValue ?? '');
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('general.year', 'Year')}
              name="year"
              onBlur={handleBlur}
              size="medium"
              fullWidth
              required
              sx={readOnlyFieldStyle}
              InputLabelProps={{
                sx: { '& .MuiFormLabel-asterisk': { color: 'error.main' } }
              }}
            />
          )}
        />
      </Grid>
      <Grid size={{ xs: 6, md: 6 }}>
        <TextField
          label={t('masterVehicle.fuelCapacity', 'Fuel Capacity')}
          name="fuel_capacity"
          value={values.fuel_capacity ?? ''}
          onChange={handleNumberChange('fuel_capacity')}
          onBlur={handleBlur}
          type="number"
          size="medium"
          fullWidth
          error={Boolean(touched.fuel_capacity && errors.fuel_capacity)}
          helperText={touched.fuel_capacity && errors.fuel_capacity}
          sx={readOnlyFieldStyle}
          required
          InputLabelProps={{
            sx: { '& .MuiFormLabel-asterisk': { color: 'error.main' } }
          }}
          InputProps={{
            endAdornment: <InputAdornment position="end">L</InputAdornment>
          }}
        />
      </Grid>
      <Grid size={{ xs: 6, md: 6 }}>
        <TextField
          label={t('masterVehicle.bodyName', 'Body Name')}
          name="name"
          value={values.name ?? ''}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.name && !!errors.name}
          helperText={touched.name && errors.name}
          size="medium"
          fullWidth
          sx={readOnlyFieldStyle}
          required
          InputLabelProps={{
            sx: { '& .MuiFormLabel-asterisk': { color: 'error.main' } }
          }}
        />
      </Grid>
      <Grid size={{ xs: 6, md: 6 }}>
        <TextField
          label={t('masterVehicle.maxSpeed', 'Max Speed')}
          name="max_speed"
          value={values.max_speed ?? ''}
          onChange={handleNumberChange('max_speed')}
          onBlur={handleBlur}
          type="number"
          size="medium"
          fullWidth
          error={Boolean(touched.max_speed && errors.max_speed)}
          helperText={touched.max_speed && errors.max_speed}
          sx={readOnlyFieldStyle}
          InputProps={{
            endAdornment: <InputAdornment position="end">km/h</InputAdornment>
          }}
        />
      </Grid>
      <Grid size={{ xs: 6, md: 6 }}>
        <TextField
          label={t('masterVehicle.chasisNo', 'Chassis Number')}
          name="chasis_no"
          value={values.chasis_no ?? ''}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.chasis_no && !!errors.chasis_no}
          helperText={touched.chasis_no && errors.chasis_no}
          size="medium"
          fullWidth
          sx={readOnlyFieldStyle}
        />
      </Grid>

      <Grid size={{ xs: 6, md: 6 }}>
        <TextField
          label={t('masterVehicle.minSpeed', 'Min Speed')}
          name="min_speed"
          value={values.min_speed ?? ''}
          onChange={handleNumberChange('min_speed')}
          onBlur={handleBlur}
          type="number"
          size="medium"
          fullWidth
          error={Boolean(touched.min_speed && errors.min_speed)}
          helperText={touched.min_speed && errors.min_speed}
          sx={readOnlyFieldStyle}
          InputProps={{
            endAdornment: <InputAdornment position="end">km/h</InputAdornment>
          }}
        />
      </Grid>
      <Grid size={{ xs: 6, md: 6 }}>
        <PickerField
          label={t('masterVehicle.vendor', 'Vendor')}
          value={values.vendor}
          {...vendorPickerParams}
          options={vendorOptions}
          onChange={(opt) => {
            const val = typeof opt === 'object' ? opt?.value : opt;
            const selected = vendorOptions.find((c) => String(c.value) === String(val)) ?? null;
            const id = selected ? selected.value : (val ?? null);
            setFieldValue('vendor', id);
            setFieldValue('vendor_name', selected ? selected.label : '');
          }}
          allowAdd
          AddComponent={VendorAddEdit}
          dialogTitle={t('masterVehicle.selectVendor', 'Select Vendor')}
          style={readOnlyFieldStyle}
          helperText={!values.company ? t('general.selectCompanyFirst', '* Please select a company first') : ''}
          disabled={!values.company}
        />
      </Grid>
      <Grid size={{ xs: 6, md: 6 }}>
        <PickerFieldTable
          label={t('masterVehicle.fuelPrice', 'Fuel Price')}
          value={values.fuel_price ?? ''}
          {...fuelPricePickerParams}
          options={fuelPriceOptions}
          listOptions={(fuelPricePickerParams.listOptions || []).filter((opt) => String(opt.id || opt.original?.id) !== '0')}
          onChange={handleFuelTypeChange}
          dialogTitle={t('masterVehicle.selectFuelPrice', 'Select Fuel Price')}
          style={readOnlyFieldStyle}
          allowAdd
          AddComponent={MasterFuelPriceAddEdit}
          helperText={!values.company ? t('general.selectCompanyFirst', '* Please select a company first') : ''}
          columns={[
            {
              field: 'fuel_type_name',
              headerName: 'Fuel Type',
              flex: 1,
              renderCell: (row) => row?.original?.fuel_type_name || row?.fuel_type_name || '-'
            },
            {
              field: 'fuel_type_code',
              headerName: 'Code',
              flex: 1,
              renderCell: (row) => row?.original?.fuel_type_code || row?.fuel_type_code || '-'
            },
            {
              field: 'valid_at',
              headerName: 'Valid At',
              flex: 1,
              renderCell: (row) => {
                const val = row?.original?.valid_at || row?.valid_at;
                if (!val || String(val).startsWith('0001-01-01') || String(val).startsWith('1900-01-01')) return '-';
                return dayjs(val).isValid() ? dayjs(val).format('DD MMM YYYY') : '-';
              }
            },
            { field: 'price', headerName: 'Price', flex: 1, renderCell: (row) => formatCurrency(row?.original?.price ?? row?.price) },
            {
              field: 'id',
              headerName: 'Fuel Price ID',
              flex: 1,
              renderCell: (row) => row?.original?.id || row?.id || '-'
            },
            {
              field: 'fuel_type_id',
              headerName: 'Fuel Type ID',
              flex: 1,
              renderCell: (row) => {
                const orig = row?.original || row;
                return (
                  (typeof orig?.fuel_type === 'object' ? orig?.fuel_type?.id : orig?.fuel_type) ||
                  orig?.fuel_type_id ||
                  orig?.fuel_type_join?.id ||
                  orig?.join?.fuel_type_id ||
                  '-'
                );
              }
            }
          ]}
        />
      </Grid>
      <Grid container size={{ xs: 6, md: 6 }}>
        <Grid size={{ xs: 6, md: 6 }}>
          <TextField
            label={t('masterVehicle.fuelRatioKm', 'Fuel Ratio KM')}
            name="fuel_ratio_km"
            value={values.fuel_ratio_km ?? ''}
            onChange={handleDecimalChange('fuel_ratio_km', 2)}
            onBlur={handleDecimalBlur('fuel_ratio_km', 2)}
            onKeyDown={(e) => {
              if (e.key === '-' || e.key === 'e' || e.key === 'E') {
                e.preventDefault();
              }
            }}
            type="number"
            size="medium"
            fullWidth
            sx={readOnlyFieldStyle}
            InputProps={{
              endAdornment: <InputAdornment position="end">KM</InputAdornment>
            }}
            inputProps={{ min: '0', step: '0.01' }}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 6 }}>
          <TextField
            label={t('masterVehicle.fuelRatioLiter', 'Fuel Ratio Liter')}
            name="fuel_ratio_litre"
            value={values.fuel_ratio_litre ?? ''}
            onChange={handleDecimalChange('fuel_ratio_litre', 2)}
            onBlur={handleDecimalBlur('fuel_ratio_litre', 2)}
            onKeyDown={(e) => {
              if (e.key === '-' || e.key === 'e' || e.key === 'E') {
                e.preventDefault();
              }
            }}
            type="number"
            size="medium"
            fullWidth
            sx={readOnlyFieldStyle}
            InputProps={{
              endAdornment: <InputAdornment position="end">L</InputAdornment>
            }}
            inputProps={{ min: '0', step: '0.01' }}
          />
        </Grid>
      </Grid>
      <Grid size={{ xs: 6, md: 6 }}>
        <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: '#fafafa' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Fuel Information
            </Typography>
            <Tooltip title="View Fuel Price History">
              <IconButton size="small" onClick={() => setFuelHistoryDialogOpen(true)} color="primary">
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Grid container spacing={1}>
            <Grid size={{ xs: 6, md: 4 }}>
              <Typography variant="caption" color="text.secondary">
                Name:
              </Typography>
              <Typography variant="body2">{values.fuel_type_name || '-'}</Typography>
            </Grid>
            <Grid size={{ xs: 6, md: 4 }}>
              <Typography variant="caption" color="text.secondary">
                Code:
              </Typography>
              <Typography variant="body2">{values.fuel_type_code || '-'}</Typography>
            </Grid>
            <Grid size={{ xs: 6, md: 4 }}>
              <Typography variant="caption" color="text.secondary">
                Category:
              </Typography>
              <Typography variant="body2">{values.fuel_type_category || '-'}</Typography>
            </Grid>
            <Grid size={{ xs: 6, md: 4 }}>
              <Typography variant="caption" color="text.secondary">
                UOM:
              </Typography>
              <Typography variant="body2">{values.fuel_type_uom || '-'}</Typography>
            </Grid>
            <Grid size={{ xs: 6, md: 4 }}>
              <Typography variant="caption" color="text.secondary">
                Desc:
              </Typography>
              <Typography variant="body2">{values.fuel_type_desc || '-'}</Typography>
            </Grid>
            <Grid size={{ xs: 6, md: 4 }}>
              <Typography variant="caption" color="text.secondary">
                Price:
              </Typography>
              <Typography variant="body2">{formatCurrency(values.fuel_price_val)}</Typography>
            </Grid>
            <Grid size={{ xs: 6, md: 4 }}>
              <Typography variant="caption" color="text.secondary">
                Valid At:
              </Typography>
              <Typography variant="body2">
                {!values.fuel_price_valid_at ||
                String(values.fuel_price_valid_at).startsWith('0001-01-01') ||
                String(values.fuel_price_valid_at).startsWith('1900-01-01')
                  ? '-'
                  : dayjs(values.fuel_price_valid_at).isValid()
                    ? dayjs(values.fuel_price_valid_at).format('DD MMM YYYY')
                    : '-'}
              </Typography>
            </Grid>
            <Grid size={{ xs: 6, md: 4 }}>
              <Typography variant="caption" color="text.secondary">
                Fuel Price ID:
              </Typography>
              <Typography variant="body2">{values.fuel_price && String(values.fuel_price) !== '0' ? values.fuel_price : '-'}</Typography>
            </Grid>
            <Grid size={{ xs: 6, md: 4 }}>
              <Typography variant="caption" color="text.secondary">
                Fuel Type ID:
              </Typography>
              <Typography variant="body2">{values.fuel_type && String(values.fuel_type) !== '0' ? values.fuel_type : '-'}</Typography>
            </Grid>
          </Grid>
        </Box>
      </Grid>
      <Grid size={{ xs: 6, md: 6 }}>
        <Box sx={{ display: 'flex', gap: 4 }}>
          {/* Vehicle Photo */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
              {t('masterVehicle.vehiclePhoto', 'Vehicle Photo')}
            </Typography>

            <Box
              sx={{
                border: '2px solid #C5D0E6',
                borderRadius: 2,
                width: 240,
                height: 180,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                position: 'relative',
                bgcolor: '#fafafa'
              }}
            >
              {vehiclePreview?.length > 0 ? (
                <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
                  {vehiclePreview.length > 1 && (
                    <Tooltip title={t('general.previous', 'Previous')}>
                      <IconButton
                        onClick={() => setCurrentImageIndex(Math.max(currentImageIndex - 1, 0))}
                        sx={{
                          position: 'absolute',
                          left: 8,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          bgcolor: 'rgba(255,255,255,0.75)',
                          backdropFilter: 'blur(4px)',
                          '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' }
                        }}
                      >
                        <NavigateBefore />
                      </IconButton>
                    </Tooltip>
                  )}

                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <img
                      src={vehiclePreview[currentImageIndex]}
                      alt={`Image ${currentImageIndex + 1}`}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        borderRadius: 4,
                        transition: '0.25s ease'
                      }}
                    />
                  </Box>

                  {vehiclePreview.length > 1 && (
                    <Tooltip title={t('general.next', 'Next')}>
                      <IconButton
                        onClick={() => setCurrentImageIndex(Math.min(currentImageIndex + 1, vehiclePreview.length - 1))}
                        sx={{
                          position: 'absolute',
                          right: 8,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          bgcolor: 'rgba(255,255,255,0.75)',
                          backdropFilter: 'blur(4px)',
                          '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' }
                        }}
                      >
                        <NavigateNext />
                      </IconButton>
                    </Tooltip>
                  )}

                  <Tooltip title={t('masterVehicle.removeImage', 'Remove image')}>
                    <IconButton
                      onClick={handleRemoveClick}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        bgcolor: 'rgba(255,255,255,0.75)',
                        padding: '4px',
                        backdropFilter: 'blur(4px)',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.95)' }
                      }}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  <Typography
                    variant="caption"
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      width: '100%',
                      textAlign: 'center',
                      color: '#444',
                      background: 'rgba(255,255,255,0.7)',
                      backdropFilter: 'blur(6px)',
                      py: 0.3
                    }}
                  >
                    {`${t('masterVehicle.image', 'Image')} ${currentImageIndex + 1} ${t('masterVehicle.of', 'of')} ${vehiclePreview.length}`}
                  </Typography>
                </Box>
              ) : (
                <></>
              )}

              {(!vehiclePreview || vehiclePreview.length === 0) && (
                <>
                  <AttachFileIcon sx={{ fontSize: 30, color: 'primary.main' }} />
                  <Typography variant="body2" color="text.secondary">
                    {t('masterVehicle.clickToUpload', 'Click to Upload')}
                  </Typography>
                  <label htmlFor="vehicle-photo-upload" style={{ position: 'absolute', inset: 0, cursor: 'pointer' }} />
                </>
              )}
            </Box>

            <input id="vehicle-photo-upload" type="file" accept="image/*" hidden onChange={handleVehicleFileChange} />
          </Box>
          {/* QR Photo */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
              QR My Pertamina
            </Typography>
            <Box
              sx={{
                border: '2px solid #C5D0E6',
                borderRadius: 2,
                width: 240,
                height: 180,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                position: 'relative',
                bgcolor: '#fafafa'
              }}
            >
              {qrCodePreview.length > 0 ? (
                <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
                  <img src={qrCodePreview} alt="QR MyPertamina" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  <Tooltip title={t('masterVehicle.removeImage', 'Remove image')}>
                    <IconButton
                      onClick={handleRemoveQrCodeClick}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        bgcolor: 'rgba(255,255,255,0.75)',
                        padding: '4px',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.95)' }
                      }}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              ) : (
                <>
                  <AttachFileIcon sx={{ fontSize: 30, color: 'primary.main' }} />
                  <Typography variant="body2" color="text.secondary">
                    {t('masterVehicle.clickToUpload', 'Click to Upload')}
                  </Typography>
                  <label htmlFor="qr-upload" style={{ position: 'absolute', inset: 0, cursor: 'pointer' }} />
                </>
              )}
              <input type="file" accept="image/*" hidden id="qr-upload" onChange={handleQrCodeFileChange} />
            </Box>
          </Box>
        </Box>
      </Grid>
      <Grid size={{ xs: 6, md: 6 }}>
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(values.is_maintenance)}
                onChange={(e) => setFieldValue('is_maintenance', e.target.checked)}
                name="is_maintenance"
              />
            }
            label={t('masterVehicle.maintenance', 'Maintenance')}
          />
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(values.disposed)}
                onChange={(e) => setFieldValue('disposed', e.target.checked)}
                name="disposed"
                disabled
              />
            }
            label={t('masterVehicle.disposed', 'Disposed')}
            disabled
          />
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(values.writteOff)}
                onChange={(e) => setFieldValue('writteOff', e.target.checked)}
                name="writteOff"
                disabled
              />
            }
            label={t('masterVehicle.writeOff', 'Write Off')}
            disabled
          />
        </Box>
      </Grid>

      {/* DIALOG HAPUS FOTO VEHICLE */}
      <DeleteConfirmDialog
        open={openDeletePhoto}
        onClose={handleCancelRemove}
        onConfirm={handleConfirmRemove}
        title={t('masterVehicle.removePhoto', 'Remove Photo')}
        message={t('masterVehicle.removePhotoConfirm', 'Are you sure you want to remove this photo?')}
        confirmLabel={t('buttonTitle.remove', 'Remove')}
        cancelLabel={t('buttonTitle.cancel', 'Cancel')}
      />

      {/* DIALOG HAPUS FOTO QR */}
      <DeleteConfirmDialog
        open={openDeleteQr}
        onClose={handleCancelRemoveQrCode}
        onConfirm={handleConfirmRemoveQrCode}
        title={t('masterVehicle.removeQrCode', 'Remove QR Code')}
        message={t('masterVehicle.removeQrCodeConfirm', 'Are you sure you want to remove this QR Code image?')}
        confirmLabel={t('buttonTitle.remove', 'Remove')}
        cancelLabel={t('buttonTitle.cancel', 'Cancel')}
      />

      {/* DIALOG RIWAYAT HARGA BBM */}
      <FuelInformationDialog
        open={fuelHistoryDialogOpen}
        onClose={() => setFuelHistoryDialogOpen(false)}
        licensePlate={values.license_plate}
        vehicleId={values.id || values.vehicle}
        vehicleName={values.name}
      />
    </Grid>
  );
});

export default InformationForm;
